<template>
  <div class="py-4 px-2">
    <div v-if="loading" class="text-center pa-6">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <template v-else>

      <!-- State A: no client-specific template yet -->
      <template v-if="!clientTemplate">
        <v-alert type="info" outlined dense class="mb-4">
          No workflow template is configured for this client. Select one below to clone it, or leave blank to use the default global template when deals are approved.
        </v-alert>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="selectedSourceId"
              :items="globalTemplates"
              item-text="name"
              item-value="id"
              label="Clone from template (optional)"
              outlined
              dense
              clearable
              :disabled="isReadOnly"
              @change="onSourceTemplateChange"
            />
          </v-col>
        </v-row>
        <template v-if="previewTasks.length">
          <div class="caption grey--text mb-2 mt-3">
            Tasks preview — editable after saving the client
          </div>
          <v-data-table
            dense
            :headers="previewHeaders"
            :items="previewTasks"
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.defaultAssigneeRoleId="{ item }">
              {{ roleLabel(item.defaultAssigneeRoleId) }}
            </template>
          </v-data-table>
        </template>
      </template>

      <!-- State B: client-specific template exists -->
      <template v-else>
        <div class="d-flex align-center mb-4">
          <div>
            <div class="caption grey--text">Workflow Template</div>
            <div class="body-1 font-weight-medium">{{ clientTemplate.name }}</div>
          </div>
          <v-spacer />
          <v-btn
            v-if="!isReadOnly"
            color="primary"
            outlined
            small
            @click="openAddTask"
          >
            <v-icon small left>add</v-icon>Add Task
          </v-btn>
        </div>
        <v-data-table
          dense
          :headers="taskHeaders"
          :items="taskList"
          class="elevation-1"
        >
          <template v-slot:item.defaultAssigneeRoleId="{ item }">
            {{ roleLabel(item.defaultAssigneeRoleId) }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn icon small @click="openEditTask(item)" :disabled="isReadOnly">
              <v-icon small>edit</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteTask(item)" :disabled="isReadOnly">
              <v-icon small color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <WorkflowTemplateTaskEdit
          v-if="showTaskDialog"
          :task="editingTask"
          :templateId="clientTemplate.id"
          :existingTasks="taskList"
          @save="handleSaveTask"
          @close="showTaskDialog = false"
        />
      </template>

    </template>
  </div>
</template>

<script>
import api from '@/lib/api'
import { PATH_WORKFLOW_TEMPLATES } from '@/lib/constants/api'
import WorkflowTemplateTaskEdit from '@/components/workflowTemplates/WorkflowTemplateTaskEdit'

const roleLabels = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  RECRUITER: 'Recruiter',
  SALES_PERSON: 'Sales Person',
  CONSULTANT: 'Consultant'
}

export default {
  components: { WorkflowTemplateTaskEdit },
  props: {
    clientId: { default: null },
    isReadOnly: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: false,
      clientTemplate: null,
      globalTemplates: [],
      selectedSourceId: null,
      previewTasks: [],
      taskList: [],
      showTaskDialog: false,
      editingTask: {},
      previewHeaders: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Role', value: 'defaultAssigneeRoleId', sortable: false, width: '18%' },
        { text: 'Anchor', value: 'dueDateAnchorName', sortable: false, width: '20%' },
        { text: 'Offset Days', value: 'dueDateOffsetDays', align: 'center', sortable: false, width: '14%' }
      ],
      taskHeaders: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Role', value: 'defaultAssigneeRoleId', sortable: false, width: '18%' },
        { text: 'Anchor', value: 'dueDateAnchorName', sortable: false, width: '20%' },
        { text: 'Offset Days', value: 'dueDateOffsetDays', align: 'center', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '10%' }
      ]
    }
  },
  methods: {
    roleLabel (roleId) {
      return roleLabels[roleId] || roleId || '—'
    },
    async loadData () {
      this.loading = true
      try {
        await this.loadGlobalTemplates()
        if (this.clientId) {
          await this.loadClientTemplate()
        }
      } finally {
        this.loading = false
      }
    },
    async loadGlobalTemplates () {
      const response = await api.doGet({ path: PATH_WORKFLOW_TEMPLATES, parameters: { isActive: true } })
      const all = response.data || []
      this.globalTemplates = all.filter(t => !t.clientId)
    },
    async loadClientTemplate () {
      const response = await api.doGet({
        path: PATH_WORKFLOW_TEMPLATES,
        parameters: { clientId: this.clientId, isActive: true }
      })
      const results = response.data || []
      if (results.length > 0) {
        this.clientTemplate = results[0]
        await this.loadTaskList()
      }
    },
    async loadTaskList () {
      const response = await api.doGet({
        path: `${PATH_WORKFLOW_TEMPLATES}/${this.clientTemplate.id}/tasks`
      })
      this.taskList = response.data || []
    },
    async onSourceTemplateChange (id) {
      this.previewTasks = []
      if (!id) {
        this.$emit('clone-pending', null)
        return
      }
      const response = await api.doGet({ path: `${PATH_WORKFLOW_TEMPLATES}/${id}` })
      const template = response.data
      this.previewTasks = (template && template.tasks) || []
      this.$emit('clone-pending', id)
    },
    openAddTask () {
      this.editingTask = {}
      this.showTaskDialog = true
    },
    openEditTask (task) {
      this.editingTask = { ...task }
      this.showTaskDialog = true
    },
    async handleSaveTask (taskData) {
      if (taskData.id) {
        await api.doPut({
          path: `${PATH_WORKFLOW_TEMPLATES}/${this.clientTemplate.id}/tasks`,
          data: taskData
        })
      } else {
        await api.doPost({
          path: `${PATH_WORKFLOW_TEMPLATES}/${this.clientTemplate.id}/tasks`,
          data: taskData
        })
      }
      this.showTaskDialog = false
      await this.loadTaskList()
    },
    async deleteTask (task) {
      if (!window.confirm(`Delete task "${task.title}"?`)) return
      await api.doDelete({
        path: `${PATH_WORKFLOW_TEMPLATES}/${this.clientTemplate.id}/tasks/${task.id}`
      })
      await this.loadTaskList()
    }
  },
  created () {
    this.loadData()
  },
  watch: {
    clientId (newVal) {
      if (newVal) {
        this.loadClientTemplate()
      }
    }
  }
}
</script>
